import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SupervisorLayout } from "@/layouts/SupervisorLayout";
import {
  Radio, Activity, PauseCircle, TestTube2, Timer, Users,
  AlertTriangle, Thermometer, CheckCircle2, Loader2, Clock, Layers,
} from "lucide-react";
import client from "@/lib/api";

// —————————————————————————————————————————————————————————————————————
// Live Monitor V1 — Supervisor command-center dashboard.
//
// Data:
//   • /api/stations                        → active station roster
//   • /api/reports/v2/live-stations         → status map (polled every ~4s)
//   • /api/stations/{id}/preview-playback   → per-station signed playback
//   • /api/orders/{id}/testing              → testing state (for TESTING cards)
//
// Performance rules honored:
//   • Iframes only for LIVE/PAUSED/TESTING (never for IDLE/AVAILABLE)
//   • Playback URL refetched only when broadcastKey changes or every 90s
//     for JWT refresh — iframe DOM node stays mounted across polls
//   • Elapsed timer ticks locally every 1s; poll every 4s only updates status
// —————————————————————————————————————————————————————————————————————

const POLL_MS = 4000;
const PLAYBACK_REFRESH_MS = 90000; // signed JWT TTL is ~90s
const LONG_PAUSE_MS = 5 * 60 * 1000;

// Cheap equality helpers so the 4s poll only triggers a re-render when the
// roster or the live-status map actually changed. This keeps already-mounted
// iframes stable and avoids unnecessary reconciler work at 6-station scale.
function sameRoster(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const x = a[i], y = b[i];
    if (x.id !== y.id || x.name !== y.name || x.active !== y.active
        || x.technician_id !== y.technician_id
        || x.technician_name !== y.technician_name
        || x.profile_photo_url !== y.profile_photo_url) return false;
  }
  return true;
}
function sameLiveMap(a, b) {
  const ak = Object.keys(a || {}), bk = Object.keys(b || {});
  if (ak.length !== bk.length) return false;
  for (const k of ak) {
    const x = a[k], y = b[k];
    if (!y) return false;
    if (x.status !== y.status || x.order_id !== y.order_id
        || x.order_number !== y.order_number
        || x.customer_name !== y.customer_name
        || x.start_time !== y.start_time
        || x.waiting_count !== y.waiting_count
        || x.current_pause_reason !== y.current_pause_reason
        || x.current_pause_started_at !== y.current_pause_started_at) return false;
  }
  return true;
}

function fmtElapsed(ms) {
  if (!ms || ms < 0) ms = 0;
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
    : `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}
const firstName = (n) => (n || "").trim().split(/\s+/)[0] || "";

// ————— Top summary tiles —————
function SummaryTile({ icon: Icon, label, value, tone = "default", testid }) {
  const tones = {
    default:  { color: "var(--accent)", bg: "rgba(0,229,255,0.05)", border: "rgba(0,229,255,0.22)" },
    live:     { color: "#00ff66",       bg: "rgba(0,255,102,0.05)", border: "rgba(0,255,102,0.28)" },
    testing:  { color: "#a78bfa",       bg: "rgba(167,139,250,0.06)", border: "rgba(167,139,250,0.28)" },
    paused:   { color: "#ff9800",       bg: "rgba(255,152,0,0.05)", border: "rgba(255,152,0,0.28)" },
    idle:     { color: "rgba(255,255,255,0.55)", bg: "rgba(255,255,255,0.02)", border: "rgba(255,255,255,0.10)" },
    pending:  { color: "#00e5ff",       bg: "rgba(0,229,255,0.05)", border: "rgba(0,229,255,0.22)" },
  };
  const t = tones[tone] || tones.default;
  return (
    <div
      className="rounded-xl border px-4 py-3 flex items-center gap-3 min-w-0"
      style={{ background: t.bg, borderColor: t.border }}
      data-testid={testid}
    >
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: "rgba(0,0,0,0.35)", color: t.color, border: `1px solid ${t.border}` }}
      >
        <Icon size={16} />
      </div>
      <div className="min-w-0">
        <div className="text-[9px] tracking-[0.25em] uppercase font-semibold text-white/50">{label}</div>
        <div className="text-white text-xl md:text-2xl font-semibold leading-none mt-1" style={{ color: t.color }}>
          {value}
        </div>
      </div>
    </div>
  );
}

function AlertsStrip({ alerts }) {
  if (!alerts.length) return null;
  return (
    <div className="mt-4 space-y-2" data-testid="livemon-alerts">
      {alerts.map((a, i) => (
        <div
          key={i}
          className="rounded-md border px-3 py-2 flex items-center gap-2 text-[12px]"
          style={{
            borderColor: a.severity === "critical" ? "rgba(255,51,51,0.4)" : "rgba(255,152,0,0.35)",
            background: a.severity === "critical" ? "rgba(255,51,51,0.06)" : "rgba(255,152,0,0.05)",
            color: a.severity === "critical" ? "#ff8888" : "#ffb066",
          }}
          data-testid={`livemon-alert-${i}`}
        >
          <AlertTriangle size={13} />
          <span className="font-semibold tracking-wide uppercase text-[10px]">{a.station_id}</span>
          <span className="opacity-90">{a.message}</span>
        </div>
      ))}
    </div>
  );
}

// Stable Cloudflare preview — refreshes signed URL only when order changes or
// every PLAYBACK_REFRESH_MS. Iframe DOM node stays mounted; only its `src`
// changes when the URL genuinely rotates.
function StationVideoPreview({ stationId, broadcastKey, active }) {
  const [src, setSrc] = useState(null);
  const lastKeyRef = useRef(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    if (!active) return;
    try {
      const { data } = await client.get(`/stations/${stationId}/preview-playback`);
      if (data?.iframe_url) {
        setSrc(`${data.iframe_url}?muted=true&autoplay=true&primaryColor=%2300e5ff&controls=false`);
      }
    } catch { /* ignore transient failures — retain last good URL */ }
  }, [stationId, active]);

  useEffect(() => {
    if (!active) { setSrc(null); return; }
    if (broadcastKey && broadcastKey !== lastKeyRef.current) {
      lastKeyRef.current = broadcastKey;
      load();
    } else if (!src) {
      load();
    }
    clearInterval(timerRef.current);
    timerRef.current = setInterval(load, PLAYBACK_REFRESH_MS);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId, broadcastKey, active]);

  if (!active) return null;
  return (
    <div
      className="relative w-full overflow-hidden rounded-lg border"
      style={{ aspectRatio: "16 / 9", background: "#000", borderColor: "rgba(0,229,255,0.18)" }}
      data-testid={`livemon-preview-${stationId}`}
    >
      {src ? (
        <iframe
          src={src}
          className="w-full h-full"
          allow="autoplay; encrypted-media; picture-in-picture;"
          title={`${stationId} preview`}
          data-testid={`livemon-iframe-${stationId}`}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-white/40 text-xs">
          <Loader2 size={16} className="animate-spin mr-2" /> loading preview…
        </div>
      )}
    </div>
  );
}

const STATUS_META = {
  LIVE:      { color: "#00ff66", border: "rgba(0,255,102,0.4)",   bg: "rgba(0,255,102,0.06)",  label: "LIVE" },
  PAUSED:    { color: "#ff9800", border: "rgba(255,152,0,0.4)",   bg: "rgba(255,152,0,0.06)",  label: "PAUSED" },
  TESTING:   { color: "#a78bfa", border: "rgba(167,139,250,0.5)", bg: "rgba(167,139,250,0.06)",label: "TESTING" },
  IDLE:      { color: "rgba(255,255,255,0.55)", border: "rgba(255,255,255,0.10)", bg: "rgba(255,255,255,0.02)", label: "AVAILABLE" },
  AVAILABLE: { color: "rgba(255,255,255,0.55)", border: "rgba(255,255,255,0.10)", bg: "rgba(255,255,255,0.02)", label: "AVAILABLE" },
  WAITING:   { color: "#ffe066", border: "rgba(255,224,102,0.35)",bg: "rgba(255,224,102,0.05)",label: "WAITING" },
};

// Compact TESTING section — mirrors CustomerLive's TestingVideoOverlay in
// language and data source, kept small enough to sit inside a station card.
function TestingStrip({ testing }) {
  if (!testing) return (
    <div className="rounded-md border px-3 py-2 text-[10px] text-white/45"
         style={{ borderColor: "rgba(167,139,250,0.25)", background: "rgba(167,139,250,0.04)" }}
         data-testid="livemon-testing-empty">
      Testing not started
    </div>
  );
  const s = testing.steps || {};
  const chip = (id, label) => {
    const status = s[id]?.status || "PENDING";
    const done = status === "COMPLETED";
    const running = status === "IN_PROGRESS";
    return (
      <div className="flex items-center justify-between text-[10px] py-0.5"
           data-testid={`livemon-testing-step-${id}`}>
        <span className="text-white/70">{label}</span>
        <span className="font-semibold flex items-center gap-1"
              style={{ color: done ? "#00ff66" : running ? "var(--accent)" : "rgba(255,255,255,0.4)" }}>
          {done ? <CheckCircle2 size={11} /> : running ? <Loader2 size={11} className="animate-spin" /> : null}
          {done ? "DONE" : running ? "RUNNING" : "PENDING"}
        </span>
      </div>
    );
  };
  const tempColor = (sev) => ({ success: "#00ff66", normal: "var(--accent)", warning: "#ff9800", critical: "#ff3333" }[sev] || "rgba(255,255,255,0.6)");
  const tempCell = (kind, temp) => {
    const has = temp && temp.value != null;
    return (
      <div className="flex items-center justify-between text-[10px] py-0.5"
           data-testid={`livemon-testing-temp-${kind}`}>
        <span className="text-white/70 flex items-center gap-1"><Thermometer size={10}/>{kind.toUpperCase()} Temp</span>
        {has ? (
          <span className="font-semibold flex items-center gap-1.5 whitespace-nowrap" style={{ color: tempColor(temp.severity) }}>
            <span className="text-white text-[11px] font-bold">{temp.value}°C</span>
            <span className="opacity-80">•</span>
            <span>{(temp.classification || "").replace(/_/g, " ")}</span>
          </span>
        ) : <span className="italic text-white/40">waiting…</span>}
      </div>
    );
  };
  return (
    <div className="rounded-md border px-3 py-2 space-y-0.5"
         style={{ borderColor: "rgba(167,139,250,0.28)", background: "rgba(167,139,250,0.04)" }}
         data-testid="livemon-testing-strip">
      <div className="text-[9px] tracking-[0.22em] uppercase font-semibold text-[color:var(--accent)] mb-1"
           style={{ color: "#a78bfa" }}>Testing & Quality Check</div>
      {chip("windows", "Windows Install")}
      {chip("gpu_driver", "GPU Driver")}
      {chip("bench", "GPU & CPU Bench")}
      <div className="h-px my-1" style={{ background: "rgba(255,255,255,0.06)" }} />
      {tempCell("gpu", testing.gpu_temp)}
      {tempCell("cpu", testing.cpu_temp)}
    </div>
  );
}

function TechnicianBadge({ name, photoUrl }) {
  const initials = (name || "?").split(/\s+/).map(p => p[0]).slice(0, 2).join("").toUpperCase() || "?";
  return (
    <div className="flex items-center gap-2" data-testid="livemon-tech">
      {photoUrl ? (
        <img src={photoUrl} alt={name || ""} className="w-7 h-7 rounded-full object-cover border" style={{ borderColor: "rgba(255,255,255,0.15)" }} />
      ) : (
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border"
             style={{ background: "rgba(0,229,255,0.08)", borderColor: "rgba(0,229,255,0.28)", color: "var(--accent)" }}>{initials}</div>
      )}
      <span className="text-[12px] text-white/85 font-medium">{name || "—"}</span>
    </div>
  );
}

function ActiveStationCard({ station, live, testing, tick }) {
  const meta = STATUS_META[live.status] || STATUS_META.IDLE;
  const startedAt = live.start_time ? new Date(live.start_time).getTime() : null;
  const localElapsed = startedAt ? tick - startedAt : (live.elapsed_ms || 0);
  const broadcastKey = live.order_id || live.order_number || "";
  return (
    <div className="rounded-xl border overflow-hidden"
         style={{ background: "#0d0d10", borderColor: meta.border }}
         data-testid={`livemon-card-${station.id}`}>
      <div className="px-4 pt-3 pb-2 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.25em] font-semibold text-white/50">{station.id}</div>
          <div className="text-white font-semibold truncate">{station.name || station.id}</div>
        </div>
        <div className="rounded-full px-2.5 py-1 text-[10px] font-bold tracking-wide flex items-center gap-1.5"
             style={{ background: meta.bg, border: `1px solid ${meta.border}`, color: meta.color }}
             data-testid={`livemon-status-${station.id}`}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
          {meta.label}
        </div>
      </div>
      <div className="px-4">
        <StationVideoPreview stationId={station.id} broadcastKey={broadcastKey} active />
      </div>
      <div className="px-4 pt-3 pb-4 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <TechnicianBadge name={station.technician_name} photoUrl={station.profile_photo_url} />
          <div className="text-white/85 text-[12px] font-mono tabular-nums flex items-center gap-1" data-testid={`livemon-timer-${station.id}`}>
            <Timer size={12} className="opacity-60" />{fmtElapsed(localElapsed)}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-[11px]">
          <div className="rounded-md border px-2.5 py-1.5" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
            <div className="text-white/45 text-[9px] uppercase tracking-widest">Order</div>
            <div className="text-white font-semibold truncate">{live.order_number || "—"}</div>
          </div>
          <div className="rounded-md border px-2.5 py-1.5" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
            <div className="text-white/45 text-[9px] uppercase tracking-widest">Customer</div>
            <div className="text-white font-semibold truncate">{firstName(live.customer_name) || "—"}</div>
          </div>
        </div>
        {live.status === "PAUSED" && (
          <div className="rounded-md border px-2.5 py-1.5 text-[11px]"
               style={{ borderColor: "rgba(255,152,0,0.3)", background: "rgba(255,152,0,0.04)", color: "#ffb066" }}
               data-testid={`livemon-pause-reason-${station.id}`}>
            Pause reason: <span className="font-semibold">{live.current_pause_reason || "unspecified"}</span>
          </div>
        )}
        {live.status === "TESTING" && <TestingStrip testing={testing} />}
      </div>
    </div>
  );
}

function IdleStationCard({ station }) {
  return (
    <div className="rounded-xl border px-4 py-4 flex items-center justify-between gap-3"
         style={{ background: "#0a0a0d", borderColor: "rgba(255,255,255,0.06)" }}
         data-testid={`livemon-card-${station.id}`}>
      <div className="min-w-0">
        <div className="text-[10px] tracking-[0.25em] font-semibold text-white/50">{station.id}</div>
        <div className="text-white font-semibold truncate">{station.name || station.id}</div>
        <div className="text-[11px] text-white/45 mt-1">Ready for next build</div>
        {station.technician_name && (
          <div className="mt-2"><TechnicianBadge name={station.technician_name} photoUrl={station.profile_photo_url} /></div>
        )}
      </div>
      <div className="rounded-full px-2.5 py-1 text-[10px] font-bold tracking-wide flex items-center gap-1.5"
           style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.10)", color: "rgba(255,255,255,0.6)" }}
           data-testid={`livemon-status-${station.id}`}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: "rgba(255,255,255,0.5)" }} />
        AVAILABLE
      </div>
    </div>
  );
}

export function LiveMonitorPage() {
  const [stations, setStations] = useState([]);
  const [liveMap, setLiveMap] = useState({});   // { [station_id]: liveRow }
  const [testingMap, setTestingMap] = useState({}); // { [order_id]: testingDoc }
  const [tick, setTick] = useState(Date.now());
  const [err, setErr] = useState(null);

  // Local 1s ticker so per-card timers stay smooth between polls.
  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // ————— Unified 4s poll: refresh station roster + live-status map together.
  //
  // Previously `/api/stations` was fetched only ONCE on mount, so any station
  // created / activated / deactivated after mount (or, in edge cases where
  // liveMap raced ahead of the stale roster, a status change on an already-
  // existing station) never propagated to the UI until the supervisor did a
  // hard browser refresh. Reconciling BOTH endpoints on the same tick
  // guarantees the roster and the status map are always mutually consistent
  // and the derived rows always reflect the latest authoritative state.
  //
  // Iframe stability is preserved by (a) using station.id as the React key so
  // React reconciles existing cards in place, and (b) StationVideoPreview
  // only refetches its signed URL when `broadcastKey` changes or every 90s —
  // NOT on every roster/status refresh.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [stationsRes, liveRes] = await Promise.all([
          client.get("/stations"),
          client.get("/reports/v2/live-stations"),
        ]);
        if (cancelled) return;
        const nextStations = Array.isArray(stationsRes.data)
          ? stationsRes.data.filter((s) => s.active !== false)
          : [];
        const nextLive = {};
        for (const row of liveRes.data?.stations || []) nextLive[row.station_id] = row;
        // Only setState if something actually changed to minimise renders.
        setStations((prev) => sameRoster(prev, nextStations) ? prev : nextStations);
        setLiveMap((prev) => sameLiveMap(prev, nextLive) ? prev : nextLive);
        setErr(null);
      } catch (e) {
        if (!cancelled) setErr(e?.message || "failed to load live monitor data");
      }
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // For every station currently in TESTING, fetch its testing doc every ~4s.
  useEffect(() => {
    let cancelled = false;
    const fetchTesting = async () => {
      const testingRows = Object.values(liveMap).filter(r => r.status === "TESTING" && r.order_id);
      if (!testingRows.length) { setTestingMap({}); return; }
      const results = await Promise.all(testingRows.map(async (r) => {
        try {
          const { data } = await client.get(`/orders/${r.order_id}/testing`);
          return [r.order_id, data];
        } catch { return [r.order_id, null]; }
      }));
      if (cancelled) return;
      setTestingMap(Object.fromEntries(results));
    };
    fetchTesting();
    const id = setInterval(fetchTesting, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [liveMap]);

  const rows = useMemo(() => {
    return stations.map(s => ({
      station: s,
      live: liveMap[s.id] || { status: "IDLE", station_id: s.id, elapsed_ms: 0, waiting_count: 0 },
    }));
  }, [stations, liveMap]);

  const summary = useMemo(() => {
    let online = 0, active = 0, testing = 0, paused = 0, available = 0, pending = 0;
    for (const { live, station } of rows) {
      if (station.active !== false) online += 1;
      if (live.status === "LIVE") active += 1;
      else if (live.status === "TESTING") testing += 1;
      else if (live.status === "PAUSED") paused += 1;
      else available += 1;
      pending += (live.waiting_count || 0);
    }
    return { online, active, testing, paused, available, pending };
  }, [rows]);

  const alerts = useMemo(() => {
    const out = [];
    for (const { live, station } of rows) {
      if (live.status === "PAUSED" && live.current_pause_started_at) {
        const dt = tick - new Date(live.current_pause_started_at).getTime();
        if (dt >= LONG_PAUSE_MS) {
          out.push({ station_id: station.id, severity: "warning", message: `Paused ${Math.floor(dt/60000)} min — ${live.current_pause_reason || "no reason set"}` });
        }
      }
      if (live.status === "TESTING") {
        const t = testingMap[live.order_id];
        for (const kind of ["gpu_temp", "cpu_temp"]) {
          const v = t?.[kind];
          if (v?.classification === "TEMP_TOO_HIGH") {
            out.push({ station_id: station.id, severity: "critical", message: `${kind === "gpu_temp" ? "GPU" : "CPU"} TEMP TOO HIGH — ${v.value}°C` });
          }
        }
      }
    }
    return out;
  }, [rows, testingMap, tick]);

  const activeRows = rows.filter(r => r.live.status === "LIVE" || r.live.status === "PAUSED" || r.live.status === "TESTING");
  const idleRows = rows.filter(r => !(r.live.status === "LIVE" || r.live.status === "PAUSED" || r.live.status === "TESTING"));

  return (
    <SupervisorLayout>
      <div className="p-5 md:p-8 max-w-[1600px]">
        <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.28em] font-semibold text-white/45 uppercase flex items-center gap-2">
              <Radio size={12} /> Build · Live
            </div>
            <h1 className="mt-2 text-2xl md:text-3xl font-semibold text-white tracking-tight" data-testid="livemon-heading">
              Live Monitor
            </h1>
            <p className="mt-1.5 text-white/50 text-sm font-normal">Real-time station wall for IDEA BUILD LAB.</p>
          </div>
          <div className="text-[10px] text-white/40 tracking-widest uppercase flex items-center gap-1" data-testid="livemon-live-badge">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#00ff66" }} />
            polling every {POLL_MS/1000}s
          </div>
        </div>

        {err && (
          <div className="mb-4 rounded-md border px-3 py-2 text-[12px]"
               style={{ borderColor: "rgba(255,51,51,0.4)", background: "rgba(255,51,51,0.05)", color: "#ff8888" }}>
            {err}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="livemon-summary">
          <SummaryTile icon={Radio}       label="Stations Online"      value={summary.online}    tone="default" testid="summary-online" />
          <SummaryTile icon={Activity}    label="Active Builds"        value={summary.active}    tone="live"    testid="summary-active" />
          <SummaryTile icon={TestTube2}   label="In Testing"           value={summary.testing}  tone="testing" testid="summary-testing" />
          <SummaryTile icon={PauseCircle} label="Paused"               value={summary.paused}    tone="paused"  testid="summary-paused" />
          <SummaryTile icon={Users}       label="Available"            value={summary.available} tone="idle"    testid="summary-available" />
          <SummaryTile icon={Layers}      label="Pending Builds Total" value={summary.pending}   tone="pending" testid="summary-pending" />
        </div>

        <AlertsStrip alerts={alerts} />

        {activeRows.length > 0 && (
          <div className="mt-6">
            <div className="text-[10px] tracking-[0.25em] uppercase font-semibold text-white/45 mb-2">Active Stations</div>
            <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4" data-testid="livemon-active-grid">
              {activeRows.map(({ station, live }) => (
                <ActiveStationCard
                  key={station.id}
                  station={station}
                  live={live}
                  testing={testingMap[live.order_id]}
                  tick={tick}
                />
              ))}
            </div>
          </div>
        )}

        {idleRows.length > 0 && (
          <div className="mt-6">
            <div className="text-[10px] tracking-[0.25em] uppercase font-semibold text-white/45 mb-2">Available Stations</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="livemon-idle-grid">
              {idleRows.map(({ station }) => (
                <IdleStationCard key={station.id} station={station} />
              ))}
            </div>
          </div>
        )}

        {rows.length === 0 && !err && (
          <div className="mt-8 rounded-xl border px-6 py-10 text-center text-white/55"
               style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0a0a0d" }}
               data-testid="livemon-empty">
            <Clock size={18} className="inline-block mr-2 opacity-70" /> No active stations configured yet.
          </div>
        )}
      </div>
    </SupervisorLayout>
  );
}
